import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { cliSessionContract } from "@common/contracts/cli-session.contract";
import type {
    ApproveCliSessionInput,
    CliSession,
} from "@common/schemas/domains/cliSession/cli-session.schema";

const cliSessionClient = getBackendAPIClient(cliSessionContract);

const cliSessionQueryKeys = {
    detail: (id: string) => ["cli-session", id] as const,
};

export function useCliSessionQuery(sessionId: string | null) {
    return useQuery({
        queryKey: cliSessionQueryKeys.detail(sessionId ?? ""),
        enabled: Boolean(sessionId),
        // Consent page stays open for a few minutes; no need to refetch
        // automatically — the page will re-query after approve/cancel
        // mutations via invalidation.
        staleTime: 15_000,
        queryFn: async (): Promise<CliSession> => {
            const result = await cliSessionClient.get({
                params: { id: sessionId! },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load CLI session (status ${result.status})`,
                );
            }
            return result.body;
        },
    });
}

export function useApproveCliSessionMutation(sessionId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: ApproveCliSessionInput) => {
            const result = await cliSessionClient.approve({
                params: { id: sessionId },
                body: input,
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to approve CLI session (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: cliSessionQueryKeys.detail(sessionId),
            });
        },
    });
}

export function useCancelCliSessionMutation(sessionId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const result = await cliSessionClient.cancel({
                params: { id: sessionId },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to cancel CLI session (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: cliSessionQueryKeys.detail(sessionId),
            });
        },
    });
}
